/**
 * Scene assembly.
 *
 * Builds a Three.js group from a compiled level — one merged mesh per semantic
 * role, plus optional overlays for the navigation graph, the boundary runs,
 * gameplay markers, room labels and geometry defects.
 */

import * as THREE from 'three';
import type { CompiledLevel, Solid, SolidRole } from 'levelspec';
import { SAMPLE_ISLAND, SAMPLE_LEDGE, type NavmeshReport } from 'levelspec';
import { MARKER_STYLE, boxToThreeBox3, geometryForSolids, materialForRole, toThree } from '../three/adapter.ts';
import type { Collider } from '../three/player.ts';

export interface LevelView {
  group: THREE.Group;
  /** Meshes keyed by semantic role, and by layer, so both can be isolated. */
  byRole: Map<SolidRole, THREE.Mesh[]>;
  byLayer: Map<string, THREE.Mesh[]>;
  dynamicIds: string[];
  colliders: Collider[];
  overlays: {
    nav: THREE.LineSegments;
    boundaries: THREE.LineSegments;
    markers: THREE.Group;
    labels: THREE.Group;
    defects: THREE.Group;
  };
  bounds: THREE.Box3;
}

export function buildLevelView(level: CompiledLevel, solids: Solid[], offenders: string[]): LevelView {
  const group = new THREE.Group();
  const byRole = new Map<SolidRole, THREE.Mesh[]>();
  const byLayer = new Map<string, THREE.Mesh[]>();

  // One merged mesh per (layer, role) pair: few enough draw calls to be cheap,
  // granular enough to isolate a storey or a semantic class on demand.
  const buckets = new Map<string, Solid[]>();
  for (const s of solids) {
    const k = `${s.layer}||${s.role}`;
    let arr = buckets.get(k);
    if (!arr) buckets.set(k, (arr = []));
    arr.push(s);
  }
  for (const [k, list] of buckets) {
    const [layerId, role] = k.split('||') as [string, SolidRole];
    const mesh = new THREE.Mesh(geometryForSolids(list), materialForRole(role));
    mesh.name = k;
    mesh.userData = { role, layer: layerId, count: list.length };
    mesh.renderOrder = role === 'glass' ? 2 : 0;
    group.add(mesh);
    if (!byRole.has(role)) byRole.set(role, []);
    byRole.get(role)!.push(mesh);
    if (!byLayer.has(layerId)) byLayer.set(layerId, []);
    byLayer.get(layerId)!.push(mesh);
  }

  const colliders: Collider[] = solids.map((s) => ({
    box: boxToThreeBox3(s.box),
    id: s.id,
    dynamic: s.dynamic,
  }));

  const bounds = new THREE.Box3();
  for (const c of colliders) bounds.union(c.box);

  return {
    group,
    byRole,
    byLayer,
    dynamicIds: solids.filter((s) => s.dynamic).map((s) => s.id),
    colliders,
    overlays: {
      nav: buildNavOverlay(level),
      boundaries: buildBoundaryOverlay(level),
      markers: buildMarkerOverlay(level),
      labels: buildLabelOverlay(level),
      defects: buildDefectOverlay(solids, offenders),
    },
    bounds,
  };
}

// ---------------------------------------------------------------------------
// Overlays
// ---------------------------------------------------------------------------

function buildNavOverlay(level: CompiledLevel): THREE.LineSegments {
  const pts: number[] = [];
  const colors: number[] = [];
  const walk = new THREE.Color(0x3fb27f);
  const portal = new THREE.Color(0xffd75e);
  const vertical = new THREE.Color(0xff7a5a);
  for (const e of level.nav.edges) {
    const a = level.nav.nodes[e.a];
    const b = level.nav.nodes[e.b];
    if (!a || !b) continue;
    const pa = toThree(a.pos[0], a.pos[1], a.pos[2] + 0.06);
    const pb = toThree(b.pos[0], b.pos[1], b.pos[2] + 0.06);
    pts.push(pa.x, pa.y, pa.z, pb.x, pb.y, pb.z);
    const c = e.kind === 'walk' ? walk : e.kind === 'portal' ? portal : vertical;
    colors.push(c.r, c.g, c.b, c.r, c.g, c.b);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
  geo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  const lines = new THREE.LineSegments(
    geo,
    new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.65, depthTest: false }),
  );
  lines.visible = false;
  lines.renderOrder = 5;
  return lines;
}

function buildBoundaryOverlay(level: CompiledLevel): THREE.LineSegments {
  const pts: number[] = [];
  const colors: number[] = [];
  const palette: Record<string, THREE.Color> = {
    hard: new THREE.Color(0xdde3ea),
    soft: new THREE.Color(0xffa640),
    reinforced: new THREE.Color(0x5b9dff),
    glass: new THREE.Color(0x7fd4e0),
    open: new THREE.Color(0x2f7f5a),
  };
  const layerZ = new Map(level.spec.layers.map((l) => [l.id, l.z]));
  for (const b of level.boundaries) {
    const z = (layerZ.get(b.layer) ?? 0) + 0.05;
    const c = palette[b.type] ?? palette.hard;
    const seg = (t0: number, t1: number, col: THREE.Color): void => {
      const p0 = b.axis === 'x' ? toThree(b.coord, t0, z) : toThree(t0, b.coord, z);
      const p1 = b.axis === 'x' ? toThree(b.coord, t1, z) : toThree(t1, b.coord, z);
      pts.push(p0.x, p0.y, p0.z, p1.x, p1.y, p1.z);
      colors.push(col.r, col.g, col.b, col.r, col.g, col.b);
    };
    let cursor = b.emitStart;
    for (const o of [...b.openings].sort((p, q) => p.t0 - q.t0)) {
      if (o.t0 > cursor) seg(cursor, Math.min(o.t0, b.emitEnd), c);
      seg(Math.max(o.t0, b.emitStart), Math.min(o.t1, b.emitEnd), new THREE.Color(0x7de08a));
      cursor = Math.max(cursor, o.t1);
    }
    if (cursor < b.emitEnd) seg(cursor, b.emitEnd, c);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
  geo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  const lines = new THREE.LineSegments(
    geo,
    new THREE.LineBasicMaterial({ vertexColors: true, depthTest: false, transparent: true, opacity: 0.9 }),
  );
  lines.visible = false;
  lines.renderOrder = 6;
  return lines;
}

function buildMarkerOverlay(level: CompiledLevel): THREE.Group {
  const g = new THREE.Group();
  for (const m of level.markers) {
    const style = MARKER_STYLE[m.kind] ?? { color: 0xffffff, label: m.kind };
    const post = new THREE.Mesh(
      new THREE.CylinderGeometry(0.12, 0.12, 2.0, 10),
      new THREE.MeshBasicMaterial({ color: style.color, transparent: true, opacity: 0.8 }),
    );
    const p = toThree(m.pos[0], m.pos[1], m.pos[2] + 1.0);
    post.position.copy(p);
    g.add(post);

    const halo = new THREE.Mesh(
      new THREE.RingGeometry(0.55, 0.85, 24),
      new THREE.MeshBasicMaterial({ color: style.color, transparent: true, opacity: 0.5, side: THREE.DoubleSide }),
    );
    halo.position.copy(toThree(m.pos[0], m.pos[1], m.pos[2] + 0.03));
    halo.rotation.x = -Math.PI / 2;
    g.add(halo);

    const label = makeLabel(m.label ?? m.id, style.color, 1, true);
    label.position.copy(toThree(m.pos[0], m.pos[1], m.pos[2] + 2.35));
    g.add(label);
  }
  return g;
}

function buildLabelOverlay(level: CompiledLevel): THREE.Group {
  const g = new THREE.Group();
  const grid = level.spec.grid ?? 1;
  for (const layer of level.spec.layers) {
    const occ = level.occupancy.get(layer.id);
    if (!occ) continue;
    const acc = new Map<string, { x: number; y: number; n: number }>();
    for (const [k, space] of occ) {
      const i = k.indexOf(',');
      const x = Number(k.slice(0, i));
      const y = Number(k.slice(i + 1));
      let a = acc.get(space);
      if (!a) acc.set(space, (a = { x: 0, y: 0, n: 0 }));
      a.x += x + 0.5;
      a.y += y + 0.5;
      a.n++;
    }
    for (const [spaceId, a] of acc) {
      const spec = layer.spaces.find((s) => s.id === spaceId);
      const label = makeLabel(spec?.label ?? spaceId, 0xffffff, 0.9, false);
      label.position.copy(toThree((a.x / a.n) * grid, (a.y / a.n) * grid, layer.z + 2.2));
      g.add(label);
    }
  }
  g.visible = false;
  return g;
}

function buildDefectOverlay(solids: Solid[], offenders: string[]): THREE.Group {
  const g = new THREE.Group();
  if (!offenders.length) return g;
  const set = new Set(offenders);
  const hit = solids.filter((s) => set.has(s.id));
  if (!hit.length) return g;
  const mesh = new THREE.Mesh(
    geometryForSolids(hit),
    new THREE.MeshBasicMaterial({
      color: 0xff3355,
      transparent: true,
      opacity: 0.42,
      depthTest: false,
      side: THREE.DoubleSide,
    }),
  );
  mesh.renderOrder = 8;
  g.add(mesh);
  g.visible = false;
  return g;
}

/**
 * The baked navmesh, drawn as one point per walkable sample: green where the
 * agent can reach it from a spawn, red where it is an island inside a declared
 * space, dim grey for roofs and ledges that were never meant to be reached.
 */
export function buildNavmeshOverlay(mesh: NavmeshReport): THREE.Points {
  const pts = mesh.points;
  const state = mesh.state;
  const geo = new THREE.BufferGeometry();
  if (!pts || !state) return new THREE.Points(geo, new THREE.PointsMaterial());
  const n = state.length;
  const pos = new Float32Array(n * 3);
  const col = new Float32Array(n * 3);
  const reach = new THREE.Color(0x3fd68a);
  const island = new THREE.Color(0xff3355);
  const ledge = new THREE.Color(0x50606f);
  for (let i = 0; i < n; i++) {
    pos[i * 3] = pts[i * 3];
    pos[i * 3 + 1] = pts[i * 3 + 2] + 0.05;
    pos[i * 3 + 2] = -pts[i * 3 + 1];
    const c = state[i] === SAMPLE_ISLAND ? island : state[i] === SAMPLE_LEDGE ? ledge : reach;
    col[i * 3] = c.r;
    col[i * 3 + 1] = c.g;
    col[i * 3 + 2] = c.b;
  }
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
  const points = new THREE.Points(
    geo,
    new THREE.PointsMaterial({ size: 0.14, vertexColors: true, transparent: true, opacity: 0.85 }),
  );
  points.visible = false;
  points.renderOrder = 4;
  return points;
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

const labelCache = new Map<string, THREE.Texture>();

export function makeLabel(text: string, color: number, scale = 1, occluded = false): THREE.Sprite {
  const key = `${text}|${color}`;
  let texture = labelCache.get(key);
  if (!texture) {
    const pad = 16;
    const font = '600 44px ui-sans-serif, system-ui, sans-serif';
    const measure = document.createElement('canvas').getContext('2d')!;
    measure.font = font;
    const w = Math.ceil(measure.measureText(text).width) + pad * 2;
    const h = 64 + pad;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d')!;
    ctx.font = font;
    ctx.textBaseline = 'middle';
    ctx.fillStyle = 'rgba(8,12,18,0.72)';
    roundRect(ctx, 0, 0, w, h, 12);
    ctx.fill();
    ctx.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
    ctx.fillText(text, pad, h / 2 + 2);
    texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    labelCache.set(key, texture);
  }
  // Screen-space sizing: a label is a label at any distance. World-space
  // sprites are unreadable across a 70 m map and absurd from 2 m away.
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: occluded,
      depthWrite: false,
      sizeAttenuation: false,
    }),
  );
  const img = texture.image as HTMLCanvasElement;
  const h = 0.028 * scale;
  sprite.scale.set((h * img.width) / img.height, h, 1);
  sprite.renderOrder = occluded ? 1 : 9;
  return sprite;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// ---------------------------------------------------------------------------
// Environment
// ---------------------------------------------------------------------------

export function buildEnvironment(scene: THREE.Scene): { sun: THREE.DirectionalLight } {
  scene.background = new THREE.Color(0x121824);
  scene.fog = new THREE.Fog(0x121824, 140, 420);

  // Greybox lighting: enough ambient to read interiors with no lightmaps,
  // enough directional contrast to tell one plane from another.
  const hemi = new THREE.HemisphereLight(0xd6e6ff, 0x3b352c, 2.1);
  scene.add(hemi);

  const sun = new THREE.DirectionalLight(0xfff4e2, 2.0);
  sun.position.set(80, 140, 55);
  scene.add(sun);

  const fill = new THREE.DirectionalLight(0x8fb6e8, 0.85);
  fill.position.set(-90, 60, -70);
  scene.add(fill);

  const rim = new THREE.DirectionalLight(0xffd9b0, 0.5);
  rim.position.set(20, -40, -80);
  scene.add(rim);

  const grid = new THREE.GridHelper(600, 150, 0x24303f, 0x161d27);
  grid.position.y = -0.4;
  (grid.material as THREE.Material).transparent = true;
  (grid.material as THREE.Material).opacity = 0.35;
  scene.add(grid);

  return { sun };
}
